import type { Schema } from "amplify/data/resource";
import {
  formatPaymentStatementDescriptor,
  normalizeBusinessName,
  phoneValidator,
  validateAddress,
  validateBusinessNameFraud,
} from "@cpos/shared";

type BusinessProfile = Schema["BusinessProfile"]["type"];
type LastStripePush = NonNullable<BusinessProfile["lastStripePush"]>;

/** How long the dialog waits for the processor's Stripe result. Unmeasured starting values; tune once real push latency is known. */
export const STRIPE_PUSH_WAIT_MS = 20_000;
export const STRIPE_PUSH_POLL_MS = 2_000;

/** Fields a save may set; `id` identifies the row. It has no `individual`, so a patch never carries personal data. */
export type BusinessProfilePatch = Pick<
  Schema["BusinessProfile"]["updateType"],
  "id" | "businessName" | "businessSupportPhone" | "company"
>;

export interface AddressDraft {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface BusinessInfoDraft {
  businessName: string;
  supportPhone: string;
  /** The business address; empty when the merchant has no `company` record. */
  address: AddressDraft;
  /** A `company` record exists, possibly holding only a phone; creating one needs a support phone. */
  hasCompanyRecord: boolean;
}

export type BusinessInfoField = "businessName" | "supportPhone" | "address";

/** One changed field. `Field` defaults to the business fields; personal info supplies its own. */
export interface FieldChange<Field extends string = BusinessInfoField> {
  field: Field;
  label: string;
  /** As stored. */
  before: string;
  /** As it will be saved. */
  after: string;
}

export interface BusinessInfoValidation {
  errors: Partial<Record<BusinessInfoField, string>>;
  /** Protected brand term the new name contains, or null. Warns, never blocks. */
  fraudTerm: string | null;
}

export type StripePushOutcome =
  | { kind: "accepted" }
  | { kind: "rejected"; message: string }
  | { kind: "noAnswer" };

/** The stored address columns shared by every nested person and company record. */
export interface AddressColumns {
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressPostalCode?: string | null;
  addressCountry?: string | null;
}

/** An address as stored on a nested record; empty when the record is missing. */
export function addressDraftFrom(
  record: AddressColumns | null | undefined,
  fallbackCountry: string | null | undefined,
): AddressDraft {
  return {
    line1: record?.addressLine1 ?? "",
    line2: record?.addressLine2 ?? "",
    city: record?.addressCity ?? "",
    state: record?.addressState ?? "",
    postalCode: record?.addressPostalCode ?? "",
    country: record?.addressCountry || fallbackCountry || "",
  };
}

/** The address as it will be saved: every part trimmed. */
export function normalizeAddress(address: AddressDraft): AddressDraft {
  return {
    line1: address.line1.trim(),
    line2: address.line2.trim(),
    city: address.city.trim(),
    state: address.state.trim(),
    postalCode: address.postalCode.trim(),
    country: address.country.trim(),
  };
}

/** The address columns to write for a normalised address; an empty line 2 clears the stored one. */
export function addressColumnsOf(address: AddressDraft) {
  return {
    addressLine1: address.line1,
    addressLine2: address.line2 || null,
    addressCity: address.city,
    addressState: address.state,
    addressPostalCode: address.postalCode,
    addressCountry: address.country,
  };
}

/** One-line form for the Review step. */
export function formatAddress(address: AddressDraft): string {
  return [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
}

/** Why a normalised address can't be saved, or null. */
export function addressError(address: AddressDraft): string | null {
  const result = validateAddress({
    addressLine1: address.line1,
    addressCity: address.city,
    addressState: address.state,
    addressPostalCode: address.postalCode,
    addressCountry: address.country,
  });
  return result.isValid ? null : (result.error ?? "Enter a complete address");
}

/** The editable values as currently stored. Reads `company` only, never the owner's `individual` record. */
export function draftFrom(business: BusinessProfile): BusinessInfoDraft {
  const { company } = business;
  return {
    businessName: business.businessName ?? "",
    supportPhone: business.businessSupportPhone ?? "",
    address: addressDraftFrom(company, business.businessCountry),
    hasCompanyRecord: !!company,
  };
}

/** The values a save would write: name canonicalised, everything trimmed. */
export function normalizeDraft(draft: BusinessInfoDraft): BusinessInfoDraft {
  return {
    businessName: normalizeBusinessName(draft.businessName),
    supportPhone: draft.supportPhone.trim(),
    address: normalizeAddress(draft.address),
    hasCompanyRecord: draft.hasCompanyRecord,
  };
}

/** Fields whose saved value would differ from the stored one. */
export function diffBusinessInfo(
  before: BusinessInfoDraft,
  edited: BusinessInfoDraft,
): FieldChange[] {
  const was = normalizeDraft(before);
  const now = normalizeDraft(edited);
  const changes: FieldChange[] = [];
  if (was.businessName !== now.businessName) {
    changes.push({
      field: "businessName",
      label: "Business name",
      before: before.businessName,
      after: now.businessName,
    });
  }
  if (was.supportPhone !== now.supportPhone) {
    changes.push({
      field: "supportPhone",
      label: "Support phone",
      before: before.supportPhone,
      after: now.supportPhone,
    });
  }
  if (JSON.stringify(was.address) !== JSON.stringify(now.address)) {
    changes.push({
      field: "address",
      label: "Business address",
      before: formatAddress(before.address),
      after: formatAddress(now.address),
    });
  }
  return changes;
}

/** Validates only the fields the edit changed, so legacy values never block an unrelated edit. */
export function validateBusinessInfo(
  before: BusinessInfoDraft,
  edited: BusinessInfoDraft,
): BusinessInfoValidation {
  const changed = new Set(diffBusinessInfo(before, edited).map((c) => c.field));
  const now = normalizeDraft(edited);
  const errors: BusinessInfoValidation["errors"] = {};
  let fraudTerm: string | null = null;

  if (changed.has("businessName")) {
    if (!now.businessName) {
      errors.businessName = "Enter the business name";
    } else {
      const fraud = validateBusinessNameFraud(now.businessName);
      if (!fraud.isValid) {
        fraudTerm = fraud.error?.match(/"([^"]+)"/)?.[1] ?? "a protected term";
      }
    }
  }

  if (changed.has("supportPhone")) {
    const phoneError = phoneValidator(now.supportPhone);
    if (phoneError) errors.supportPhone = phoneError;
  }

  if (changed.has("address")) {
    const addressProblem = addressError(now.address);
    if (addressProblem) {
      errors.address = addressProblem;
    } else if (!before.hasCompanyRecord && !now.supportPhone) {
      errors.address = "Add a support phone to create a business address";
    }
  }

  return { errors, fraudTerm };
}

/** The "This will also" lines for the Review step. */
export function effectsOf(changes: FieldChange[]): string[] {
  const renamed = changes.find((c) => c.field === "businessName");
  const mayReverify = changes.some(
    (c) => c.field === "businessName" || c.field === "address",
  );
  return [
    ...(renamed
      ? [
          `change card statements to ${formatPaymentStatementDescriptor(renamed.after)}`,
        ]
      : []),
    mayReverify
      ? "update Stripe, which may ask the merchant to re-verify"
      : "update Stripe",
    "show on terminals after Sync Now or a restart",
    "leave email receipts already sent unchanged",
  ];
}

/**
 * Only the changed fields. The address is merged into the existing `company`
 * record, or creates one (with the support phone) when the merchant has none.
 */
export function buildBusinessProfilePatch(
  business: BusinessProfile,
  edited: BusinessInfoDraft,
  changes: FieldChange[],
): BusinessProfilePatch {
  const now = normalizeDraft(edited);
  const changed = new Set(changes.map((c) => c.field));
  const patch: BusinessProfilePatch = { id: business.id };

  if (changed.has("businessName")) patch.businessName = now.businessName;
  if (changed.has("supportPhone")) {
    patch.businessSupportPhone = now.supportPhone;
  }

  if (changed.has("address")) {
    const fields = addressColumnsOf(now.address);
    patch.company = business.company
      ? { ...business.company, ...fields }
      : {
          ...fields,
          phone: changed.has("supportPhone")
            ? now.supportPhone
            : business.businessSupportPhone || now.supportPhone,
        };
  }

  return patch;
}

/** Null until the processor records a push for this save (`pushedAt >= savedAt`, both server clocks). */
export function stripePushOutcome(
  push:
    | Pick<LastStripePush, "pushedAt" | "accepted" | "rejectionMessage">
    | null
    | undefined,
  savedAt: string,
): StripePushOutcome | null {
  if (!push || !(Date.parse(push.pushedAt) >= Date.parse(savedAt))) return null;
  return push.accepted
    ? { kind: "accepted" }
    : {
        kind: "rejected",
        message: push.rejectionMessage || "Stripe rejected the change",
      };
}
